import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import {
  Snowflake, Flame, Sun, Clock, Power, PauseCircle, PlayCircle, StopCircle,
  Ban, Sparkles, FileText, Upload, Trash2, Plus, History, CheckCircle2, XCircle,
  SkipForward, ChevronDown, Save, LibraryBig, Bot, Pencil, Users, ExternalLink,
  ChevronRight, X, MessageSquare, CalendarClock, AlertTriangle, UserCheck,
} from "lucide-react";

const STAGE_META = {
  cold: { icon: Snowflake, color: "text-sky-600", bg: "bg-sky-50", ring: "border-sky-200", label: "Cold" },
  warm: { icon: Sun, color: "text-yellow-600", bg: "bg-yellow-50", ring: "border-yellow-200", label: "Warm" },
  hot: { icon: Flame, color: "text-red-600", bg: "bg-red-50", ring: "border-red-200", label: "Hot" },
};
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const EVENT_META = {
  sent: { color: "text-[#25D366]", icon: CheckCircle2 },
  scheduled: { color: "text-[#8696A0]", icon: Clock },
  enrolled: { color: "text-[#128C7E]", icon: UserCheck },
  cancelled: { color: "text-red-500", icon: XCircle },
  customer_replied: { color: "text-red-500", icon: MessageSquare },
  admin_replied: { color: "text-red-500", icon: MessageSquare },
  removed: { color: "text-red-500", icon: Ban },
  skipped: { color: "text-amber-500", icon: SkipForward },
  paused: { color: "text-amber-500", icon: PauseCircle },
  resume: { color: "text-[#25D366]", icon: PlayCircle },
  failed: { color: "text-red-600", icon: AlertTriangle },
  temperature_changed: { color: "text-purple-500", icon: Sparkles },
};

function Toggle({ on, onChange, testid, size = "md" }) {
  const w = size === "sm" ? "w-9 h-5" : "w-11 h-6";
  const dot = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  const shift = size === "sm" ? (on ? "translate-x-[18px]" : "translate-x-0.5") : (on ? "translate-x-[22px]" : "translate-x-0.5");
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!on)}
      className={`relative inline-flex ${w} shrink-0 items-center rounded-full transition-colors ${on ? "bg-[#25D366]" : "bg-[#CBD5E1]"}`}>
      <span className={`inline-block ${dot} transform rounded-full bg-white shadow transition-transform ${shift}`} />
    </button>
  );
}

function fmt(v) {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? "—" : d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}
function istClock() {
  return new Date().toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}
function tempBadge(cat) {
  if (["Hot", "Very Hot"].includes(cat)) return "bg-red-50 text-red-600 border-red-200";
  if (cat === "Warm") return "bg-yellow-50 text-yellow-600 border-yellow-200";
  return "bg-sky-50 text-sky-600 border-sky-200";
}
const LABEL_NAME = { cold_followup: "Cold Follow-up", warm_followup: "Warm Follow-up", hot_followup: "Hot Follow-up" };

export default function Automations() {
  const [reengage, setReengage] = useState(null);
  const navigate = useNavigate();
  const [autos, setAutos] = useState([]);
  const [settings, setSettings] = useState(null);
  const [dash, setDash] = useState(null);
  const [leads, setLeads] = useState([]);
  const [selLead, setSelLead] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [kb, setKb] = useState([]);
  const [manual, setManual] = useState({ title: "", content: "" });
  const [tpls, setTpls] = useState([]);
  const [editSteps, setEditSteps] = useState(null); // automation_id currently expanded
  const [draft, setDraft] = useState([]);
  const [clock, setClock] = useState(istClock());
  const [enrollments, setEnrollments] = useState([]);
  const [enrolledStage, setEnrolledStage] = useState(null); // 'cold'|'warm'|'hot' panel open
  const [detail, setDetail] = useState(null); // lead automation detail modal
  const [logStage, setLogStage] = useState("all");
  const [logStatus, setLogStatus] = useState("all");
  const [openLog, setOpenLog] = useState(null); // expanded activity item log_id
  const [activityTab, setActivityTab] = useState("activity"); // 'activity' | 'today'
  const [today, setToday] = useState(null);
  const [logs, setLogs] = useState([]);

  const loadLogs = () => {
    const p = new URLSearchParams();
    if (logStage !== "all") p.set("stage", logStage);
    if (logStatus !== "all") p.set("status", logStatus);
    api.get(`/automation/logs?${p.toString()}`).then((r) => setLogs(r.data || [])).catch(() => {});
  };
  useEffect(() => { loadLogs(); /* eslint-disable-next-line */ }, [logStage, logStatus]);

  useEffect(() => {
    const id = setInterval(() => setClock(istClock()), 1000);
    return () => clearInterval(id);
  }, []);

  const loadEnrollments = () => api.get("/automation/enrollments").then((r) => setEnrollments(r.data || [])).catch(() => {});
  const loadToday = () => api.get("/automation/today").then((r) => setToday(r.data)).catch(() => {});

  const load = () => {
    api.get("/automation/list").then((r) => setAutos(r.data || [])).catch(() => {});
    api.get("/automation/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/automation/dashboard").then((r) => setDash(r.data)).catch(() => {});
    api.get("/automation/kb").then((r) => setKb(r.data || [])).catch(() => {});
    api.get("/automation/templates").then((r) => setTpls(r.data || [])).catch(() => {});
    loadEnrollments();
    loadToday();
  };
  useEffect(() => {
    load();
    api.get("/leads").then((r) => setLeads(r.data || [])).catch(() => {});
  }, []);

  const enrolledByStage = (stage) => enrollments.filter((e) => e.stage === stage && ["active", "paused"].includes(e.status));
  const openInbox = (convId) => { if (convId) navigate(`/inbox?conversation=${convId}`); };
  const openDetail = async (leadId) => {
    try { const r = await api.get(`/automation/lead/${leadId}/detail`); setDetail(r.data); }
    catch { toast.error("Could not load lead"); }
  };
  const enrollmentAction = async (leadId, action) => {
    try {
      await api.post(`/automation/lead/${leadId}/${action}`, {});
      toast.success(`Automation ${action === "remove" ? "removed" : action + "d"}`);
      loadEnrollments(); load();
      if (detail?.lead?.lead_id === leadId) openDetail(leadId);
    } catch { toast.error("Action failed"); }
  };

  const toggleAuto = async (a) => {
    try {
      await api.put(`/automation/item/${a.automation_id}`, { enabled: !a.enabled });
      toast.success(`${a.name} ${!a.enabled ? "enabled" : "disabled"}`);
      load();
    } catch { toast.error("Failed to update"); }
  };
  const saveSettings = async (patch) => {
    setSettings({ ...settings, ...patch });
    try { await api.put("/automation/settings", patch); load(); }
    catch { toast.error("Failed to save settings"); }
  };
  const toggleDay = (d) => {
    const days = new Set(settings.days || []);
    days.has(d) ? days.delete(d) : days.add(d);
    saveSettings({ days: [...days].sort() });
  };

  // ---- step builder ----
  const openSteps = (a) => {
    setEditSteps(a.automation_id);
    setDraft((a.steps || []).map((s) => ({ ...s, mode: s.mode || "ai" })));
  };
  const updStep = (i, patch) => setDraft(draft.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const addStep = () => setDraft([...draft, { delay_hours: 24, mode: "ai", goal: "Follow up using the conversation context.", meta_template: "", meta_language: "en_US" }]);
  const rmStep = (i) => setDraft(draft.filter((_, idx) => idx !== i));
  const saveSteps = async (a) => {
    try {
      await api.put(`/automation/item/${a.automation_id}`, { steps: draft, max_touches: draft.length });
      toast.success("Sequence saved");
      setEditSteps(null); load();
    } catch { toast.error("Failed to save sequence"); }
  };

  // ---- templates ----
  const saveTpl = async (t) => {
    try { await api.put(`/automation/templates/${t.template_id}`, { name: t.name, stage: t.stage, body: t.body }); toast.success("Template saved"); }
    catch { toast.error("Failed"); }
  };
  const addTpl = async () => {
    try { await api.post("/automation/templates", { name: "New Template", stage: "cold", body: "Hi {name}, ..." }); toast.success("Template added"); load(); }
    catch { toast.error("Failed"); }
  };
  const delTpl = async (id) => { try { await api.delete(`/automation/templates/${id}`); load(); } catch { toast.error("Failed"); } };
  const setTplField = (id, patch) => setTpls(tpls.map((t) => t.template_id === id ? { ...t, ...patch } : t));

  const runPreview = async () => {
    if (!selLead) { toast.error("Pick a lead first"); return; }
    setPreviewing(true);
    try { const r = await api.post(`/automation/preview/${selLead}`); setPreview(r.data); }
    catch { toast.error("Preview failed"); }
    setPreviewing(false);
  };
  const leadAction = async (action, payload) => {
    if (!selLead) return;
    try { await api.post(`/automation/lead/${selLead}/${action}`, payload || {}); toast.success(`Lead ${action} done`); runPreview(); load(); }
    catch { toast.error("Action failed"); }
  };
  const addManual = async () => {
    if (!manual.content.trim()) { toast.error("Content required"); return; }
    try { await api.post("/automation/kb/manual", manual); setManual({ title: "", content: "" }); toast.success("Added to Automation KB"); load(); }
    catch { toast.error("Failed to add"); }
  };
  const uploadKb = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/automation/kb/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File uploaded"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };
  const delKb = async (id) => { try { await api.delete(`/automation/kb/${id}`); load(); } catch { toast.error("Failed"); } };

  const statusIcon = (s) => s === "sent" ? <CheckCircle2 className="h-3.5 w-3.5 text-[#25D366]" />
    : s === "cancelled" ? <XCircle className="h-3.5 w-3.5 text-red-500" />
    : s === "skipped" ? <SkipForward className="h-3.5 w-3.5 text-amber-500" />
    : <Clock className="h-3.5 w-3.5 text-[#8696A0]" />;
  void statusIcon;

  return (
    <>
      <PageHeader eyebrow="MuviArts Workspace" title="Automations" subtitle="AI-assisted lead nurturing"
        actions={<span className="flex items-center gap-1.5 text-xs font-mono text-[#075E54] bg-[#E7FCE8] rounded-full px-3 py-1.5" data-testid="automation-clock"><Clock className="h-3.5 w-3.5" /> {clock} IST</span>} />
      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-8 space-y-6">

        {/* Global control + business hours */}
        <div className="border border-[#E9EDEF] bg-white rounded-lg p-4 md:p-5" data-testid="automation-settings">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Power className={`h-5 w-5 shrink-0 ${settings?.global_enabled ? "text-[#25D366]" : "text-[#8696A0]"}`} />
              <div className="min-w-0">
                <h3 className="font-heading text-sm font-semibold text-[#111B21]">Automation Engine</h3>
                <p className="text-[11px] text-[#8696A0]">Master switch. Follow-ups only send during business hours.</p>
              </div>
            </div>
            {settings && <Toggle testid="automation-global-toggle" on={settings.global_enabled} onChange={(v) => saveSettings({ global_enabled: v })} />}
          </div>
          {settings && (
            <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4 pt-4 border-t border-[#E9EDEF]">
              <div className="flex items-center gap-2 flex-wrap">
                <Clock className="h-4 w-4 text-[#8696A0]" />
                <span className="text-xs text-[#54656F]">Window (IST)</span>
                <input type="time" value={settings.business_start} data-testid="business-start"
                  onChange={(e) => saveSettings({ business_start: e.target.value })}
                  className="text-xs border border-[#E9EDEF] rounded-md px-2 py-1" />
                <span className="text-xs text-[#8696A0]">to</span>
                <input type="time" value={settings.business_end} data-testid="business-end"
                  onChange={(e) => saveSettings({ business_end: e.target.value })}
                  className="text-xs border border-[#E9EDEF] rounded-md px-2 py-1" />
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {DAYS.map((d, i) => (
                  <button key={d} onClick={() => toggleDay(i)}
                    className={`text-[10px] px-2 py-1 rounded-md font-medium transition-colors ${(settings.days || []).includes(i) ? "bg-[#075E54] text-white" : "bg-[#F0F2F5] text-[#8696A0]"}`}>{d}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 28 Oct 2026: anti-spam limits, payment reminder, slow re-engagement */}
        {settings?.limits && (
          <div className="border border-[#E9EDEF] bg-white rounded-lg p-4 md:p-5 space-y-4" data-testid="automation-limits">
            <div>
              <h3 className="font-heading text-sm font-semibold text-[#111B21]">Safe sending (after Meta's spam warning)</h3>
              <p className="text-[11px] text-[#8696A0]">Today: {settings.sent_today} automated messages sent ({settings.templates_today} templates). Replies typed by you or by Vivaan to a customer's own message are not counted.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-xs text-[#54656F] flex items-center justify-between gap-2">Max automated messages / day
                <input type="number" min={0} max={100} defaultValue={settings.limits.daily_cap} data-testid="limit-daily"
                  onBlur={(e) => saveSettings({ daily_cap: Number(e.target.value) })} className="w-20 text-xs border border-[#E9EDEF] rounded-md px-2 py-1" /></label>
              <label className="text-xs text-[#54656F] flex items-center justify-between gap-2">Of which templates (people quiet 24 h+)
                <input type="number" min={0} max={50} defaultValue={settings.limits.template_daily_cap} data-testid="limit-templates"
                  onBlur={(e) => saveSettings({ template_daily_cap: Number(e.target.value) })} className="w-20 text-xs border border-[#E9EDEF] rounded-md px-2 py-1" /></label>
            </div>
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#E9EDEF]">
              <div className="min-w-0">
                <p className="text-sm text-[#111B21]">Payment reminder</p>
                <p className="text-[11px] text-[#8696A0]">One gentle reminder if someone got the payment link and went quiet. Never a second one, never after they write back.</p>
              </div>
              <div className="flex items-center gap-2">
                <select defaultValue={settings.limits.payment_followup_hours} onChange={(e) => saveSettings({ payment_followup_hours: Number(e.target.value) })}
                  className="text-xs border border-[#E9EDEF] rounded-md px-2 py-1" data-testid="payment-hours">
                  {[1, 2, 3, 4].map((h) => <option key={h} value={h}>after {h} h</option>)}
                </select>
                <Toggle testid="payment-reminder-toggle" on={settings.limits.payment_followup_enabled} onChange={(v) => saveSettings({ payment_followup_enabled: v })} />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#E9EDEF]">
              <div className="min-w-0">
                <p className="text-sm text-[#111B21]">Re-open old chats (slowly)</p>
                <p className="text-[11px] text-[#8696A0]">One polite approved message ("still want to go ahead? 1 yes / 2 later / 3 not now") to people who wrote to us in the last 60 days and went quiet. Max {settings.limits.reengage_daily_cap} a day, one every 10 min, 11:00–19:00, once per person in 30 days.</p>
                {reengage && <p className="text-[11px] text-[#54656F] mt-1" data-testid="reengage-preview">{reengage.count} people are eligible right now.</p>}
              </div>
              <div className="flex items-center gap-2">
                <button className="text-xs border border-[#CBD5E1] rounded-md px-2 py-1" onClick={() => api.get("/automation/reengage/preview").then((r) => setReengage(r.data)).catch(() => toast.error("Preview failed"))}>Preview</button>
                <Toggle testid="reengage-toggle" on={settings.limits.reengage_enabled} onChange={(v) => {
                  if (v && !window.confirm("Start re-opening old chats? Max " + settings.limits.reengage_daily_cap + " a day, one approved message each.")) return;
                  saveSettings({ reengage_enabled: v });
                }} />
              </div>
            </div>
          </div>
        )}

        {/* Dashboard stats — real backend analytics */}
        {dash?.analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="automation-dash">
            {[
              ["Active enrolled", dash.analytics.active_enrolled],
              ["Due today", dash.analytics.due_today],
              ["Messages sent", dash.analytics.messages_sent],
              ["Delivery rate", `${dash.analytics.delivery_rate}%`],
              ["Reply rate", `${dash.analytics.reply_rate}%`],
              ["Replied → cancelled", dash.analytics.cancelled_customer_replied],
              ["Failed", dash.analytics.failed],
              ["Completed", dash.analytics.completed],
            ].map(([l, v]) => (
              <div key={l} className="border border-[#E9EDEF] bg-white rounded-lg p-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#8696A0]">{l}</p>
                <p className="font-heading text-2xl font-light text-[#111B21] mt-1">{v}</p>
              </div>
            ))}
          </div>
        )}

        {/* Automations + step builder */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {autos.map((a) => {
            const m = STAGE_META[a.stage] || STAGE_META.cold;
            const expanded = editSteps === a.automation_id;
            return (
              <div key={a.automation_id} className={`border ${m.ring} bg-white rounded-lg p-4 md:p-5 flex flex-col`} data-testid={`automation-card-${a.stage}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className={`h-10 w-10 rounded-xl ${m.bg} grid place-items-center ${m.color}`}><m.icon className="h-5 w-5" /></div>
                  <Toggle testid={`automation-toggle-${a.stage}`} on={a.enabled} onChange={() => toggleAuto(a)} />
                </div>
                <h3 className="mt-3 font-heading text-sm font-semibold text-[#111B21]">{a.name}</h3>
                <p className="text-xs text-[#8696A0] mt-1 leading-relaxed">{a.description}</p>

                {!expanded && (
                  <div className="mt-3 space-y-1.5 flex-1">
                    {(a.steps || []).map((s, i) => (
                      <div key={i} className="flex items-start gap-2 text-[11px]">
                        <span className="shrink-0 font-mono text-[#8696A0] mt-0.5">{s.delay_hours >= 24 ? `${Math.round(s.delay_hours / 24)}d` : `${s.delay_hours}h`}</span>
                        <span className="shrink-0">{s.mode === "template" ? <Pencil className="h-3 w-3 text-amber-500 mt-0.5" /> : <Bot className="h-3 w-3 text-[#128C7E] mt-0.5" />}</span>
                        <span className="text-[#54656F] leading-snug">{s.mode === "template" ? (s.template || "(manual message)") : s.goal}</span>
                      </div>
                    ))}
                  </div>
                )}

                {expanded && (
                  <div className="mt-3 space-y-3 flex-1" data-testid={`step-editor-${a.stage}`}>
                    {draft.map((s, i) => (
                      <div key={i} className="rounded-lg border border-[#E9EDEF] p-2.5 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-[#8696A0]">Wait</span>
                          <input type="number" min="1" value={s.delay_hours} onChange={(e) => updStep(i, { delay_hours: Number(e.target.value) })}
                            className="w-16 text-xs border border-[#E9EDEF] rounded px-1.5 py-1" />
                          <span className="text-[10px] text-[#8696A0]">hrs</span>
                          <button onClick={() => rmStep(i)} className="ml-auto text-[#8696A0] hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-[#8696A0]">AI writes</span>
                          <Toggle size="sm" on={(s.mode || "ai") === "ai"} onChange={(v) => updStep(i, { mode: v ? "ai" : "template" })} testid={`step-mode-${a.stage}-${i}`} />
                          <span className="text-[10px] text-[#8696A0]">{(s.mode || "ai") === "ai" ? "AI (dynamic)" : "Manual"}</span>
                        </div>
                        {(s.mode || "ai") === "ai"
                          ? <textarea value={s.goal || ""} onChange={(e) => updStep(i, { goal: e.target.value })} rows={2}
                              placeholder="Goal for the AI (it summarises the chat + KB and writes the message)"
                              className="w-full text-xs border border-[#E9EDEF] rounded px-2 py-1.5" />
                          : <textarea value={s.template || ""} onChange={(e) => updStep(i, { template: e.target.value })} rows={2}
                              placeholder="Manual message. Use {name}, {service}"
                              className="w-full text-xs border border-[#E9EDEF] rounded px-2 py-1.5" />}
                        <div className="rounded-md bg-[#F0F6F4] border border-[#DCE7E3] p-2 space-y-1.5">
                          <p className="text-[10px] text-[#54656F] leading-snug">
                            <span className="font-semibold text-[#075E54]">Outside the 24h window</span> only this
                            approved Meta template is sent (never AI/free text). Inside 24h, the message above is used.
                          </p>
                          <div className="flex flex-col sm:flex-row gap-1.5">
                            <input value={s.meta_template || ""} onChange={(e) => updStep(i, { meta_template: e.target.value.trim() })}
                              data-testid={`step-meta-template-${a.stage}-${i}`}
                              placeholder="muviarts_..._v1"
                              className="flex-1 text-[11px] font-mono border border-[#E9EDEF] rounded px-2 py-1" />
                            <input value={s.meta_language || "en_US"} onChange={(e) => updStep(i, { meta_language: e.target.value.trim() })}
                              data-testid={`step-meta-language-${a.stage}-${i}`}
                              placeholder="en_US"
                              className="w-full sm:w-24 text-[11px] font-mono border border-[#E9EDEF] rounded px-2 py-1" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <button onClick={addStep} className="text-[11px] flex items-center gap-1 border border-[#E9EDEF] rounded-md px-2 py-1 hover:bg-[#F0F2F5]"><Plus className="h-3 w-3" /> Step</button>
                      <button onClick={() => saveSteps(a)} data-testid={`save-steps-${a.stage}`} className="text-[11px] flex items-center gap-1 bg-[#075E54] text-white rounded-md px-3 py-1 hover:bg-[#128C7E]"><Save className="h-3 w-3" /> Save</button>
                      <button onClick={() => setEditSteps(null)} className="text-[11px] text-[#8696A0] px-2 py-1">Cancel</button>
                    </div>
                  </div>
                )}

                {!expanded && (
                  <div className="mt-3 flex flex-col gap-2">
                    <button onClick={() => openSteps(a)} data-testid={`edit-steps-${a.stage}`}
                      className="text-[11px] flex items-center gap-1 text-[#075E54] hover:text-[#128C7E]">
                      <ChevronDown className="h-3.5 w-3.5" /> Edit sequence · {a.steps?.length || 0} touches
                    </button>
                    <button onClick={() => setEnrolledStage(enrolledStage === a.stage ? null : a.stage)}
                      data-testid={`enrolled-toggle-${a.stage}`}
                      className={`text-[11px] flex items-center gap-1.5 rounded-md px-2 py-1.5 border transition-colors ${enrolledStage === a.stage ? `${m.bg} ${m.ring} ${m.color}` : "border-[#E9EDEF] text-[#54656F] hover:bg-[#F0F2F5]"}`}>
                      <Users className="h-3.5 w-3.5" />
                      {enrolledByStage(a.stage).length} lead{enrolledByStage(a.stage).length === 1 ? "" : "s"} enrolled
                      <ChevronRight className={`h-3.5 w-3.5 ml-auto transition-transform ${enrolledStage === a.stage ? "rotate-90" : ""}`} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Enrolled leads panel (per stage) */}
        {enrolledStage && (
          <div className={`border ${STAGE_META[enrolledStage].ring} bg-white rounded-lg overflow-hidden`} data-testid={`enrolled-panel-${enrolledStage}`}>
            <div className={`flex items-center justify-between gap-2 px-5 py-3.5 border-b border-[#E9EDEF] ${STAGE_META[enrolledStage].bg}`}>
              <div className="flex items-center gap-2">
                <Users className={`h-4 w-4 ${STAGE_META[enrolledStage].color}`} />
                <h3 className="font-heading text-sm font-semibold text-[#111B21]">
                  {STAGE_META[enrolledStage].label} — Enrolled Leads ({enrolledByStage(enrolledStage).length})
                </h3>
              </div>
              <button onClick={() => setEnrolledStage(null)} className="text-[#8696A0] hover:text-[#111B21]"><X className="h-4 w-4" /></button>
            </div>
            <div className="divide-y divide-[#E9EDEF] max-h-96 overflow-y-auto">
              {enrolledByStage(enrolledStage).length === 0 && (
                <p className="px-5 py-8 text-sm text-[#8696A0] text-center">No leads enrolled in this automation yet.</p>
              )}
              {enrolledByStage(enrolledStage).map((e) => (
                <div key={e.enrollment_id} className="px-4 py-3 flex items-center gap-3 hover:bg-[#F8FAFC]" data-testid={`enrolled-row-${e.lead_id}`}>
                  <button onClick={() => openDetail(e.lead_id)} className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[#111B21] truncate">{e.name}</span>
                      <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-md border ${tempBadge(e.temperature)}`}>{e.temperature}</span>
                      {e.status === "paused" && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-600 border border-amber-200">Paused</span>}
                    </div>
                    <p className="text-[11px] text-[#8696A0] mt-0.5">
                      Step {e.current_step}/{e.total_steps} · Next: {fmt(e.next_run_at)}
                      {e.last_customer_message && <span className="hidden sm:inline"> · Last reply: {fmt(e.last_customer_message.created_at)}</span>}
                    </p>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    {e.status === "active"
                      ? <button title="Pause" onClick={() => enrollmentAction(e.lead_id, "pause")} className="p-1.5 rounded-md hover:bg-[#F0F2F5] text-[#8696A0]"><PauseCircle className="h-4 w-4" /></button>
                      : <button title="Resume" onClick={() => enrollmentAction(e.lead_id, "resume")} className="p-1.5 rounded-md hover:bg-[#F0F2F5] text-[#25D366]"><PlayCircle className="h-4 w-4" /></button>}
                    <button title="Remove from automation" onClick={() => enrollmentAction(e.lead_id, "remove")} className="p-1.5 rounded-md hover:bg-red-50 text-[#8696A0] hover:text-red-500"><Ban className="h-4 w-4" /></button>
                    <button title="Open in WhatsApp Inbox" onClick={() => openInbox(e.conversation_id)} data-testid={`open-inbox-${e.lead_id}`} className="p-1.5 rounded-md hover:bg-[#E7FCE8] text-[#075E54]"><ExternalLink className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preview / test */}
        <div className="border border-[#E9EDEF] bg-white rounded-lg p-4 md:p-5" data-testid="automation-preview">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-[#128C7E]" />
            <h3 className="font-heading text-sm font-semibold text-[#111B21]">Preview & Test (no message is sent)</h3>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <select value={selLead} onChange={(e) => { setSelLead(e.target.value); setPreview(null); }} data-testid="preview-lead-select"
              className="text-sm border border-[#E9EDEF] rounded-md px-3 py-2 sm:min-w-[220px]">
              <option value="">Select a lead...</option>
              {leads.map((l) => <option key={l.lead_id} value={l.lead_id}>{(l.name || l.phone)} - {l.category}</option>)}
            </select>
            <button onClick={runPreview} disabled={previewing} data-testid="preview-run"
              className="text-sm font-semibold bg-[#075E54] text-white rounded-md px-4 py-2 hover:bg-[#128C7E] disabled:opacity-50">
              {previewing ? "Generating..." : "Preview"}
            </button>
          </div>
          {preview && !preview.error && (
            <div className="mt-4 rounded-lg border border-[#E9EDEF] bg-[#F9FAFB] p-4" data-testid="preview-result">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-3">
                <span>Temp: <b className="uppercase">{preview.temperature}</b></span>
                <span>Automation: <b>{preview.automation}</b>{preview.automation_enabled ? "" : " (off)"}</span>
                <span>Next: <b>{preview.next_action}</b></span>
                <span>Scheduled: <b>{fmt(preview.scheduled_for)}</b></span>
              </div>
              <p className="text-[11px] text-[#8696A0] mb-2">{preview.temperature_reason}</p>
              <div className="rounded-lg bg-[#D9FDD3] px-3 py-2 text-sm text-[#111B21]" data-testid="preview-message">{preview.preview_message}</div>
              {preview.service_window && (
                <div className="mt-3 rounded-lg border border-[#E9EDEF] bg-white p-3 text-[11px]" data-testid="preview-service-window">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`font-mono px-1.5 py-0.5 rounded-md border ${preview.service_window.outside_24h ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-[#DCF8C6] text-[#075E54] border-[#25D366]/40"}`}>
                      {preview.service_window.outside_24h ? "OUTSIDE 24h" : "INSIDE 24h"}
                    </span>
                    <span className="text-[#54656F]">Route: <b>{preview.service_window.route}</b></span>
                  </div>
                  {preview.service_window.outside_24h ? (
                    <div className="space-y-0.5 text-[#54656F]">
                      <p>Mapped template: <b className="font-mono">{preview.service_window.mapped_template || "—"}</b> ({preview.service_window.mapped_language})</p>
                      <p>Status: <b className={preview.service_window.eligible ? "text-[#075E54]" : "text-red-600"}>{preview.service_window.template_status}</b></p>
                      {preview.service_window.eligible
                        ? <p className="text-[#075E54]">Would send: {preview.service_window.would_send}</p>
                        : <p className="text-red-600">Would skip — {preview.service_window.skip_reason}</p>}
                    </div>
                  ) : (
                    <p className="text-[#54656F]">Inside the window the free-form AI/manual message above is used.</p>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => leadAction("pause")} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E9EDEF] hover:bg-[#F0F2F5]"><PauseCircle className="h-3.5 w-3.5" /> Pause</button>
                <button onClick={() => leadAction("resume")} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E9EDEF] hover:bg-[#F0F2F5]"><PlayCircle className="h-3.5 w-3.5" /> Resume</button>
                <button onClick={() => leadAction("stop")} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E9EDEF] hover:bg-[#F0F2F5]"><StopCircle className="h-3.5 w-3.5" /> Stop</button>
                <button onClick={() => leadAction("exclude")} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E9EDEF] hover:bg-[#F0F2F5]"><Ban className="h-3.5 w-3.5" /> Exclude</button>
                <div className="flex items-center gap-1 sm:ml-auto">
                  <span className="text-[11px] text-[#8696A0]">Override:</span>
                  {["Cold", "Warm", "Hot"].map((t) => (
                    <button key={t} onClick={() => leadAction("temperature", { temperature: t })}
                      className="text-[11px] px-2 py-1 rounded-md border border-[#E9EDEF] hover:bg-[#F0F2F5]">{t}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Template library */}
        <div className="border border-[#E9EDEF] bg-white rounded-lg p-4 md:p-5" data-testid="automation-templates">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <LibraryBig className="h-4 w-4 text-[#128C7E]" />
              <h3 className="font-heading text-sm font-semibold text-[#111B21] truncate">Template Library</h3>
            </div>
            <button onClick={addTpl} className="text-xs font-semibold flex items-center gap-1 border border-[#E9EDEF] rounded-md px-2.5 py-1.5 hover:bg-[#F0F2F5] shrink-0"><Plus className="h-3.5 w-3.5" /> New</button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {tpls.map((t) => {
              const m = STAGE_META[t.stage] || STAGE_META.cold;
              return (
                <div key={t.template_id} className="rounded-lg border border-[#E9EDEF] p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input value={t.name} onChange={(e) => setTplField(t.template_id, { name: e.target.value })}
                      className="flex-1 text-xs font-semibold border border-[#E9EDEF] rounded px-2 py-1" />
                    <select value={t.stage} onChange={(e) => setTplField(t.template_id, { stage: e.target.value })}
                      className={`text-[10px] rounded px-1.5 py-1 border ${m.ring} ${m.color}`}>
                      <option value="cold">Cold</option><option value="warm">Warm</option><option value="hot">Hot</option>
                    </select>
                  </div>
                  <textarea value={t.body} onChange={(e) => setTplField(t.template_id, { body: e.target.value })} rows={2}
                    className="w-full text-xs border border-[#E9EDEF] rounded px-2 py-1.5" />
                  <div className="flex items-center gap-2">
                    <button onClick={() => saveTpl(t)} className="text-[11px] flex items-center gap-1 bg-[#075E54] text-white rounded px-2.5 py-1 hover:bg-[#128C7E]"><Save className="h-3 w-3" /> Save</button>
                    <button onClick={() => delTpl(t.template_id)} className="text-[11px] text-[#8696A0] hover:text-red-500 flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Automation KB */}
        <div className="border border-[#E9EDEF] bg-white rounded-lg p-4 md:p-5" data-testid="automation-kb">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-4 w-4 text-[#128C7E]" />
            <h3 className="font-heading text-sm font-semibold text-[#111B21]">Automation Follow-up Knowledge Base</h3>
          </div>
          <p className="text-[11px] text-[#8696A0] mb-4">Separate from Vivaan's KB. The AI reads these when writing nurture messages.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <input placeholder="Title (optional)" value={manual.title} data-testid="kb-title"
                onChange={(e) => setManual({ ...manual, title: e.target.value })}
                className="w-full text-sm border border-[#E9EDEF] rounded-md px-3 py-2" />
              <textarea placeholder="Paste automation knowledge / rules here..." value={manual.content} data-testid="kb-content"
                onChange={(e) => setManual({ ...manual, content: e.target.value })} rows={4}
                className="w-full text-sm border border-[#E9EDEF] rounded-md px-3 py-2" />
              <div className="flex flex-wrap gap-2">
                <button onClick={addManual} data-testid="kb-add" className="text-xs font-semibold flex items-center gap-1 bg-[#075E54] text-white rounded-md px-3 py-2 hover:bg-[#128C7E]"><Plus className="h-3.5 w-3.5" /> Add text</button>
                <label className="text-xs font-semibold flex items-center gap-1 border border-[#E9EDEF] rounded-md px-3 py-2 cursor-pointer hover:bg-[#F0F2F5]">
                  <Upload className="h-3.5 w-3.5" /> Upload PDF/DOCX/TXT
                  <input type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={uploadKb} data-testid="kb-upload" />
                </label>
              </div>
            </div>
            <div className="space-y-1.5">
              {kb.length === 0 && <p className="text-xs text-[#8696A0]">No automation KB documents yet.</p>}
              {kb.map((d) => (
                <div key={d.document_id} className="flex items-center justify-between gap-2 text-xs border border-[#E9EDEF] rounded-md px-3 py-2">
                  <span className="truncate">{d.title} <span className="text-[#8696A0]">· {d.chunks} chunks</span></span>
                  <button onClick={() => delKb(d.document_id)} className="text-[#8696A0] hover:text-red-500 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Activity + Today's follow-ups */}
        <div className="border border-[#E9EDEF] bg-white rounded-lg" data-testid="automation-log">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-[#E9EDEF]">
            <div className="flex items-center gap-1">
              <button onClick={() => setActivityTab("activity")} data-testid="tab-activity"
                className={`text-sm font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 ${activityTab === "activity" ? "bg-[#E7FCE8] text-[#075E54]" : "text-[#8696A0] hover:bg-[#F0F2F5]"}`}>
                <History className="h-4 w-4" /> Activity
              </button>
              <button onClick={() => { setActivityTab("today"); loadToday(); }} data-testid="tab-today"
                className={`text-sm font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 ${activityTab === "today" ? "bg-[#E7FCE8] text-[#075E54]" : "text-[#8696A0] hover:bg-[#F0F2F5]"}`}>
                <CalendarClock className="h-4 w-4" /> Today's Follow-ups
              </button>
            </div>
            {activityTab === "activity" && (
              <div className="flex items-center gap-2">
                <select value={logStage} onChange={(e) => setLogStage(e.target.value)} data-testid="log-filter-stage"
                  className="text-xs border border-[#E9EDEF] rounded-md px-2 py-1">
                  <option value="all">All temps</option><option value="cold">Cold</option><option value="warm">Warm</option><option value="hot">Hot</option>
                </select>
                <select value={logStatus} onChange={(e) => setLogStatus(e.target.value)} data-testid="log-filter-status"
                  className="text-xs border border-[#E9EDEF] rounded-md px-2 py-1">
                  {["all", "scheduled", "enrolled", "sent", "cancelled", "skipped", "paused", "resume", "failed", "temperature_changed"].map((s) =>
                    <option key={s} value={s}>{s === "all" ? "All events" : s}</option>)}
                </select>
              </div>
            )}
          </div>

          {activityTab === "activity" && (
            <div className="divide-y divide-[#E9EDEF] max-h-[28rem] overflow-y-auto">
              {logs.length === 0 && (
                <p className="px-5 py-8 text-sm text-[#8696A0] text-center">No automation activity for this filter yet.</p>
              )}
              {logs.map((l) => {
                const ev = EVENT_META[l.event_type] || EVENT_META[l.status] || { color: "text-[#8696A0]", icon: Clock };
                const Icon = ev.icon;
                const isOpen = openLog === l.log_id;
                return (
                  <div key={l.log_id} data-testid="activity-item">
                    <button onClick={() => setOpenLog(isOpen ? null : l.log_id)} className="w-full flex items-start gap-3 px-5 py-3 text-left hover:bg-[#F8FAFC]">
                      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${ev.color}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-[#111B21]">
                          <b className="capitalize">{(l.event_type || l.status).replace(/_/g, " ")}</b>
                          {l.stage && <span> · {l.stage}</span>}
                          {typeof l.step_index === "number" && <span> · step {l.step_index + 1}</span>}
                          {l.reason && <span className="text-[#8696A0]"> — {l.reason}</span>}
                        </p>
                        {l.message && <p className="text-xs text-[#8696A0] truncate">{l.message}</p>}
                      </div>
                      <span className="text-[10px] text-[#8696A0] shrink-0">{fmt(l.created_at)}</span>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-4 pt-1 ml-7 space-y-2 text-xs bg-[#F8FAFC]" data-testid="activity-detail">
                        {l.message && (
                          <div className="rounded-lg bg-[#D9FDD3] px-3 py-2 text-[#111B21]">{l.message}</div>
                        )}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[#54656F]">
                          <span>Event: <b className="capitalize">{(l.event_type || l.status).replace(/_/g, " ")}</b></span>
                          <span>Temperature: <b className="capitalize">{l.stage || "—"}</b></span>
                          <span>Step: <b>{typeof l.step_index === "number" ? l.step_index + 1 : "—"}</b></span>
                          <span>Delivery: <b>{l.delivery_status || l.status}</b></span>
                          <span>Created: <b>{fmt(l.created_at)}</b></span>
                          {l.scheduled_at && <span>Scheduled: <b>{fmt(l.scheduled_at)}</b></span>}
                          {l.sent_at && <span>Sent: <b>{fmt(l.sent_at)}</b></span>}
                          {l.provider_message_id && <span className="truncate col-span-2">WA msg id: <b className="font-mono">{l.provider_message_id}</b></span>}
                        </div>
                        <div className="flex flex-wrap gap-2 pt-1">
                          {l.lead_id && <button onClick={() => openDetail(l.lead_id)} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md border border-[#E9EDEF] hover:bg-white"><Users className="h-3 w-3" /> Lead detail</button>}
                          {l.conversation_id && <button onClick={() => openInbox(l.conversation_id)} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md border border-[#E9EDEF] hover:bg-white text-[#075E54]"><ExternalLink className="h-3 w-3" /> Open in Inbox</button>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {activityTab === "today" && (
            <div className="max-h-[28rem] overflow-y-auto" data-testid="today-panel">
              <div className="px-5 py-3 border-b border-[#E9EDEF] bg-[#F8FAFC]">
                <p className="text-xs font-semibold text-[#111B21]">Due today ({today?.due?.length || 0})</p>
              </div>
              <div className="divide-y divide-[#E9EDEF]">
                {(!today?.due || today.due.length === 0) && <p className="px-5 py-5 text-xs text-[#8696A0] text-center">No follow-ups due today.</p>}
                {(today?.due || []).map((e) => (
                  <div key={e.enrollment_id} className="px-5 py-3 flex items-center gap-3 hover:bg-[#F8FAFC]">
                    <CalendarClock className="h-4 w-4 text-[#128C7E] shrink-0" />
                    <button onClick={() => openDetail(e.lead_id)} className="flex-1 min-w-0 text-left">
                      <p className="text-xs font-semibold text-[#111B21] truncate">{e.name} <span className={`ml-1 text-[9px] font-mono px-1.5 py-0.5 rounded-md border ${tempBadge(e.temperature)}`}>{e.temperature}</span></p>
                      <p className="text-[11px] text-[#8696A0]">Step {e.current_step}/{e.total_steps} · Due {fmt(e.next_run_at)}</p>
                    </button>
                    <button onClick={() => openInbox(e.conversation_id)} className="p-1.5 rounded-md hover:bg-[#E7FCE8] text-[#075E54]"><ExternalLink className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <div className="px-5 py-3 border-y border-[#E9EDEF] bg-[#F8FAFC]">
                <p className="text-xs font-semibold text-[#111B21]">Today's activity ({today?.activity?.length || 0})</p>
              </div>
              <div className="divide-y divide-[#E9EDEF]">
                {(!today?.activity || today.activity.length === 0) && <p className="px-5 py-5 text-xs text-[#8696A0] text-center">No activity yet today.</p>}
                {(today?.activity || []).map((l) => {
                  const ev = EVENT_META[l.event_type] || EVENT_META[l.status] || { color: "text-[#8696A0]", icon: Clock };
                  const Icon = ev.icon;
                  return (
                    <div key={l.log_id} className="flex items-start gap-3 px-5 py-2.5">
                      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${ev.color}`} />
                      <p className="text-xs text-[#111B21] flex-1 min-w-0">
                        <b className="capitalize">{(l.event_type || l.status).replace(/_/g, " ")}</b>
                        {l.stage && <span> · {l.stage}</span>}
                        {l.reason && <span className="text-[#8696A0]"> — {l.reason}</span>}
                      </p>
                      <span className="text-[10px] text-[#8696A0] shrink-0">{fmt(l.created_at)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Lead automation detail modal */}
        {detail && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={() => setDetail(null)} data-testid="lead-detail-modal">
            <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#E9EDEF] sticky top-0 bg-white">
                <h3 className="font-heading text-sm font-semibold text-[#111B21]">Lead automation</h3>
                <button onClick={() => setDetail(null)} className="text-[#8696A0] hover:text-[#111B21]"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-full bg-[#25D366] grid place-items-center text-white font-semibold">{(detail.contact?.name || "?")[0]?.toUpperCase()}</div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#111B21] truncate">{detail.contact?.name || detail.lead?.name}</p>
                    <p className="font-mono text-[11px] text-[#8696A0]">{detail.contact?.phone || detail.lead?.phone}</p>
                  </div>
                  <span className={`ml-auto text-[10px] font-mono px-2 py-1 rounded-md border ${tempBadge(detail.lead?.category)}`}>{detail.lead?.category}</span>
                </div>

                {detail.follow_up_label && (
                  <div className="text-xs"><span className="text-[#8696A0]">Follow-up label: </span><b>{LABEL_NAME[detail.follow_up_label]}</b></div>
                )}

                {detail.automation ? (
                  <div className="rounded-lg border border-[#E9EDEF] p-3 space-y-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-[#111B21]">{detail.automation.name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-md ${detail.enrollment?.status === "active" ? "bg-[#E7FCE8] text-[#075E54]" : detail.enrollment?.status === "paused" ? "bg-amber-50 text-amber-600" : "bg-[#F0F2F5] text-[#8696A0]"}`}>{detail.enrollment?.status}</span>
                    </div>
                    <p className="text-[#54656F]">Step {detail.automation.current_step}/{detail.automation.total_steps} · Next: {fmt(detail.enrollment?.next_run_at)}</p>
                    <p className="text-[#8696A0]">Enrolled: {fmt(detail.enrollment?.created_at)} · Source: {detail.enrollment?.source || "auto"}</p>
                    {detail.enrollment?.stop_reason && <p className="text-red-500">{detail.enrollment.stop_reason}</p>}
                    <div className="flex flex-wrap gap-2 pt-1">
                      {detail.enrollment?.status === "active"
                        ? <button onClick={() => enrollmentAction(detail.lead.lead_id, "pause")} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md border border-[#E9EDEF] hover:bg-[#F0F2F5]"><PauseCircle className="h-3 w-3" /> Pause</button>
                        : <button onClick={() => enrollmentAction(detail.lead.lead_id, "resume")} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md border border-[#E9EDEF] hover:bg-[#F0F2F5]"><PlayCircle className="h-3 w-3" /> Resume</button>}
                      <button onClick={() => enrollmentAction(detail.lead.lead_id, "remove")} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md border border-[#E9EDEF] hover:bg-red-50 text-red-500"><Ban className="h-3 w-3" /> Remove</button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-[#8696A0]">Not currently enrolled in any automation.</p>
                )}

                <div>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-[#8696A0] mb-2">Conversation preview</p>
                  <div className="rounded-lg border border-[#E9EDEF] p-3 space-y-1.5 max-h-56 overflow-y-auto bg-[#F8FAFC]">
                    {(detail.conversation_preview || []).length === 0 && <p className="text-xs text-[#8696A0]">No messages.</p>}
                    {(detail.conversation_preview || []).map((m, i) => {
                      const cust = m.sender_type === "CUSTOMER";
                      return (
                        <div key={i} className={`flex ${cust ? "justify-start" : "justify-end"}`}>
                          <div className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-xs ${cust ? "bg-white border border-[#E9EDEF] text-[#111B21]" : "bg-[#D9FDD3] text-[#111B21]"}`}>
                            {m.automated && <span className="block text-[9px] text-[#128C7E] font-semibold mb-0.5">Automated</span>}
                            {m.content}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <button onClick={() => openInbox(detail.lead?.conversation_id)} data-testid="detail-open-inbox"
                  className="w-full flex items-center justify-center gap-2 bg-[#075E54] text-white hover:bg-[#128C7E] rounded-xl px-4 py-2.5 text-sm font-semibold">
                  <ExternalLink className="h-4 w-4" /> Open in WhatsApp Inbox
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}
